import addEnhancedLocationsAsyncApiMethod from './location/addEnhancedLocationsAsyncApiMethod';
import addFileAttachmentAsyncApiMethod from './attachments/addFileAttachmentAsyncApiMethod';
import addItemAttachmentAsyncApiMethod from './attachments/addItemAttachmentAsyncApiMethod';
import addFileAttachmentFromBase64AsyncApiMethod from './attachments/addFileAttachmentFromBase64AsyncApiMethod';
import addNotificationMessageAsyncApiMethod from './notification/addNotificationMessageAsyncApiMethod';
import addRecipientsAsyncApiMethod from './recipients/addRecipientsAsyncApiMethod';
import ApiErrorCode from './ApiErrorCode';
import appendOnSendAsyncApiMethod from './body/appendOnSendAsyncApiMethod';
import bodyPrependAsyncApiMethod from './body/bodyPrependAsyncApiMethod';
import closeApiMethod from './close/closeApiMethod';
import closeAppApiMethod from './close/closeAppApiMethod';
import datapoints from '../datapoints';
import displayAppointmentFormApiMethod from './display/displayAppointmentFormApiMethod';
import displayMessageFormApiMethod from './display/displayMessageFormApiMethod';
import displayNewAppointmentFormApiMethod from './display/displayNewAppointmentFormApiMethod';
import displayNewMessageFormApiMethod from './display/displayNewMessageFormApiMethod';
import displayReplyAllFormApiMethod from './display/displayReplyAllFormApiMethod';
import displayReplyFormApiMethod from './display/displayReplyFormApiMethod';
import doesItemExist from '../utils/doesItemExist';
import eventCompletedApiMethod from './eventCompleted/eventCompletedApiMethod';
import ewsRequestApiMethod from './ews/ewsRequestApiMethod';
import getAccessTokenApiMethod from './sso/getAccessTokenApiMethod';
import getAllNotificationMessagesAsyncApiMethod from './notification/getAllNotificationMessagesAsyncApiMethod';
import getAttachmentsAsyncApiMethod from './attachments/getAttachmentsAsyncApiMethod';
import getBodyAsyncApiMethod from './body/getBodyAsyncApiMethod';
import getBodyTypeAsyncApiMethod from './body/getBodyTypeAsyncApiMethod';
import getCallbackTokenApiMethod from './token/getCallbackTokenApiMethod';
import getEnhancedLocationsAsyncApiMethod from './location/getEnhancedLocationsAsyncApiMethod';
import getFromAsyncApiMethod from './from/getFromAsyncApiMethod';
import getInitialDataApiMethod from './getInitialData/getInitialDataApiMethod';
import getInitializationContextApiMethod from './getInitialData/getInitializationContextApiMethod';
import getInternetHeadersAsyncApiMethod from './internetHeaders/getInternetHeadersAsyncApiMethod';
import getLocationAsyncApiMethod from './location/getLocationAsyncApiMethod';
import getRecipientsAsyncApiMethod from './recipients/getRecipientsAsyncApiMethod';
import getRecurrenceAsyncApiMethod from './recurrence/getRecurrenceAsyncApiMethod';
import getSelectedDataAsyncApiMethod from './selectedData/getSelectedDataAsyncApiMethod';
import getSharedPropertiesAsyncApiMethod from './sharedProperties/getSharedPropertiesApiMethod';
import getSubjectAsyncApiMethod from './subject/getSubjectAsyncApiMethod';
import getTimeAsyncApiMethod from './time/getTimeAsyncApiMethod';
import getUserIdentityTokenApiMethod from './token/getUserIdentityTokenApiMethod';
import loadCustomPropertiesApiMethod from './customProperties/loadCustomPropertiesApiMethod';
import messageParentApiMethod from './dialog/messageParentApiMethod';
import registerConsentAsyncApiMethod from './consent/registerConsentAsyncApiMethod';
import removeAttachmentAsyncApiMethod from './attachments/removeAttachmentAsyncApiMethod';
import removeEnhancedLocationsAsyncApiMethod from './location/removeEnhancedLocationsAsyncApiMethod';
import removeInternetHeadersAsyncApiMethod from './internetHeaders/removeInternetHeadersAsyncApiMethod';
import removeNotificationMessageAsyncApiMethod from './notification/removeNotificationMessageAsyncApiMethod';
import replaceNotificationMessageAsyncApiMethod from './notification/replaceNotificationMessageAsyncApiMethod';
import saveAsyncApiMethod from './save/saveAsyncApiMethod';
import saveCustomPropertiesApiMethod from './customProperties/saveCustomPropertiesApiMethod';
import saveSettingsRequestApiMethod from './settings/saveSettingsRequestApiMethod';
import setBodyAsyncApiMethod from './body/setBodyAsyncApiMethod';
import setBodySelectedDataAsyncApiMethod from './selectedData/setBodySelectedDataAsyncApiMethod';
import setInternetHeadersAsyncApiMethod from './internetHeaders/setInternetHeadersAsyncApiMethod';
import setLocationAsyncApiMethod from './location/setLocationAsyncApiMethod';
import setRecipientsAsyncApiMethod from './recipients/setRecipientsAsyncApiMethod';
import setRecurrenceAsyncApiMethod from './recurrence/setRecurrenceAsyncApiMethod';
import setSelectedDataAsyncApiMethod from './selectedData/setSelectedDataAsyncApiMethod';
import setSubjectAsyncApiMethod from './subject/setSubjectAsyncApiMethod';
import setTimeAsyncApiMethod from './time/setTimeAsyncApiMethod';
import { ApiMethodCallback } from './ApiMethod';
import { createErrorResult } from './ApiMethodResponseCreator';
import { logUsage } from 'owa-analytics';
import { OutlookMethodDispId } from './OutlookMethodDispId';
import {
    getAddinCommandForControl,
    IAddinCommand,
    getEntryPointForControl,
    getScenarioFromHostItemIndex,
} from 'owa-addins-store';
import { getClientApp, getUserCulture } from 'owa-session-store';

export interface ApiMethodDelegate {
    (
        hostItemIndex: string,
        controlId: string,
        data: any,
        createErrorResultToLog: ApiMethodCallback
    ): void;
}

export const apiMethods: { [key: number]: ApiMethodDelegate } = {
    [OutlookMethodDispId.GetInitialData]: getInitialDataApiMethod,
    [OutlookMethodDispId.EwsRequest]: ewsRequestApiMethod,
    [OutlookMethodDispId.GetRecipientsAsync]: getRecipientsAsyncApiMethod,
    [OutlookMethodDispId.GetSubjectAsync]: getSubjectAsyncApiMethod,
    [OutlookMethodDispId.SetSubjectAsync]: setSubjectAsyncApiMethod,
    [OutlookMethodDispId.SetRecipientsAsync]: setRecipientsAsyncApiMethod,
    [OutlookMethodDispId.AddRecipientsAsync]: addRecipientsAsyncApiMethod,
    [OutlookMethodDispId.SaveSettingsRequest]: saveSettingsRequestApiMethod,
    [OutlookMethodDispId.GetAccessTokenAsync]: getAccessTokenApiMethod,
    [OutlookMethodDispId.GetCallbackToken]: getCallbackTokenApiMethod,
    [OutlookMethodDispId.GetUserIdentityToken]: getUserIdentityTokenApiMethod,
    [OutlookMethodDispId.MESSAGE_PARENT_DISPID]: messageParentApiMethod,
    [OutlookMethodDispId.SetBodyAsync]: setBodyAsyncApiMethod,
    [OutlookMethodDispId.GetBodyAsync]: getBodyAsyncApiMethod,
    [OutlookMethodDispId.GetBodyTypeAsync]: getBodyTypeAsyncApiMethod,
    [OutlookMethodDispId.BodyPrependAsync]: bodyPrependAsyncApiMethod,
    [OutlookMethodDispId.Close]: closeApiMethod,
    [OutlookMethodDispId.CloseApp]: closeAppApiMethod,
    [OutlookMethodDispId.CloseContainer]: closeAppApiMethod,
    [OutlookMethodDispId.DisplayMessageForm]: displayMessageFormApiMethod,
    [OutlookMethodDispId.DisplayReplyAllForm]: displayReplyAllFormApiMethod,
    [OutlookMethodDispId.DisplayReplyAllFormWithAttachments]: displayReplyAllFormApiMethod,
    [OutlookMethodDispId.DisplayReplyForm]: displayReplyFormApiMethod,
    [OutlookMethodDispId.DisplayReplyFormWithAttachments]: displayReplyFormApiMethod,
    [OutlookMethodDispId.DisplayAppointmentForm]: displayAppointmentFormApiMethod,
    [OutlookMethodDispId.DisplayNewAppointmentForm]: displayNewAppointmentFormApiMethod,
    [OutlookMethodDispId.DisplayNewMessageForm]: displayNewMessageFormApiMethod,
    [OutlookMethodDispId.EventCompleted]: eventCompletedApiMethod,
    [OutlookMethodDispId.GetSelectedDataAsync]: getSelectedDataAsyncApiMethod,
    [OutlookMethodDispId.SetSelectedDataAsync]: setSelectedDataAsyncApiMethod,
    [OutlookMethodDispId.SetBodySelectedDataAsync]: setBodySelectedDataAsyncApiMethod,
    [OutlookMethodDispId.SaveAsync]: saveAsyncApiMethod,
    [OutlookMethodDispId.LoadCustomProperties]: loadCustomPropertiesApiMethod,
    [OutlookMethodDispId.SaveCustomProperties]: saveCustomPropertiesApiMethod,
    [OutlookMethodDispId.AddNotificationMessageAsync]: addNotificationMessageAsyncApiMethod,
    [OutlookMethodDispId.GetAllNotificationMessagesAsync]: getAllNotificationMessagesAsyncApiMethod,
    [OutlookMethodDispId.RemoveNotificationMessageAsync]: removeNotificationMessageAsyncApiMethod,
    [OutlookMethodDispId.ReplaceNotificationMessageAsync]: replaceNotificationMessageAsyncApiMethod,
    [OutlookMethodDispId.AddItemAttachmentAsync]: addItemAttachmentAsyncApiMethod,
    [OutlookMethodDispId.AddFileAttachmentAsync]: addFileAttachmentAsyncApiMethod,
    [OutlookMethodDispId.AddBase64FileAttachmentAsync]: addFileAttachmentFromBase64AsyncApiMethod,
    [OutlookMethodDispId.RemoveAttachmentAsync]: removeAttachmentAsyncApiMethod,
    [OutlookMethodDispId.GetAttachmentsAsync]: getAttachmentsAsyncApiMethod,
    [OutlookMethodDispId.RegisterConsentAsync]: registerConsentAsyncApiMethod,
    [OutlookMethodDispId.GetLocationAsync]: getLocationAsyncApiMethod,
    [OutlookMethodDispId.SetLocationAsync]: setLocationAsyncApiMethod,
    [OutlookMethodDispId.GetTimeAsync]: getTimeAsyncApiMethod,
    [OutlookMethodDispId.SetTimeAsync]: setTimeAsyncApiMethod,
    [OutlookMethodDispId.GetInitializationContextAsync]: getInitializationContextApiMethod,
    [OutlookMethodDispId.GetFromAsync]: getFromAsyncApiMethod,
    [OutlookMethodDispId.GetSharedPropertiesAsync]: getSharedPropertiesAsyncApiMethod,
    [OutlookMethodDispId.GetRecurrenceAsync]: getRecurrenceAsyncApiMethod,
    [OutlookMethodDispId.SetRecurrenceAsync]: setRecurrenceAsyncApiMethod,
    [OutlookMethodDispId.GetInternetHeadersAsync]: getInternetHeadersAsyncApiMethod,
    [OutlookMethodDispId.SetInternetHeadersAsync]: setInternetHeadersAsyncApiMethod,
    [OutlookMethodDispId.RemoveInternetHeadersAsync]: removeInternetHeadersAsyncApiMethod,
    [OutlookMethodDispId.AppendOnSendAsync]: appendOnSendAsyncApiMethod,
    [OutlookMethodDispId.GetEnhancedLocationsAsync]: getEnhancedLocationsAsyncApiMethod,
    [OutlookMethodDispId.AddEnhancedLocationsAsync]: addEnhancedLocationsAsyncApiMethod,
    [OutlookMethodDispId.RemoveEnhancedLocationsAsync]: removeEnhancedLocationsAsyncApiMethod,
};
export let createErrorResultToLog; //Making it global to import it for testing in executeApiMethodTests
export default function executeApiMethod(
    hostItemIndex: string,
    dispId: OutlookMethodDispId,
    controlId: string,
    data: any,
    callback: ApiMethodCallback
): void {
    const method = apiMethods[dispId];
    if (!method) {
        return;
    }
    const addinCommand: IAddinCommand = getAddinCommandForControl(controlId);
    const { name, customData } = datapoints.ExecuteApiMethod.datapoint;
    if (!doesItemExist(hostItemIndex)) {
        const { name, customData } = datapoints.ExecuteApiMethodNoItemFailure.datapoint;
        logUsage(name, customData(dispId));

        callback(createErrorResult(ApiErrorCode.GenericResponseError));
        return;
    }

    try {
        createErrorResultToLog = response => {
            let errorCode: ApiErrorCode = null;
            if (response) {
                if (response.errorCode) {
                    errorCode = response.errorCode;
                }
            }
            logUsage(
                name,
                customData(
                    dispId,
                    addinCommand.extension.Id,
                    addinCommand.extension.DisplayName,
                    getClientApp(),
                    getScenarioFromHostItemIndex(hostItemIndex),
                    getEntryPointForControl(controlId),
                    getUserCulture(),
                    addinCommand.extension.Type,
                    errorCode
                )
            );
            callback(response);
        };
        method(hostItemIndex, controlId, data, createErrorResultToLog);
    } catch (error) {
        logUsage(
            name,
            customData(
                dispId,
                addinCommand.extension.Id,
                addinCommand.extension.DisplayName,
                getClientApp(),
                getScenarioFromHostItemIndex(hostItemIndex),
                getEntryPointForControl(controlId),
                getUserCulture(),
                addinCommand.extension.Type,
                null, //errorCode
                JSON.stringify(error)
            )
        );
        callback(createErrorResult(ApiErrorCode.InternalServerError));
    }
}
